import { IComponentOptions } from "angular";

export type PaginationMode = "default" | "compact";

export type Page = number | "_";

// The number of pages to show, including the first and last pages.
// It should be an odd number. If there are more pages, ellipsis will
// be shown
const maxShownPages = 7;

const sequence = (start: number, size: number): number[] => Array.from(Array(size)).map((num, i) => start + i);

export class PaginationCtrl {
  mode: PaginationMode = "default";
  current: number;
  total: number;
  pageChange: ({ page: number }) => void;

  goTo(page: number): void {
    if (page !== this.current && page >= 1 && page <= this.total) {
      this.current = page;
      this.pageChange({ page });
    }
  }

  canGoPrev(): boolean {
    return this.current > 1;
  }

  goPrev(): void {
    return this.canGoPrev() && this.goTo(this.current - 1);
  }

  canGoNext(): boolean {
    return this.current < this.total;
  }

  goNext(): void {
    return this.canGoNext() && this.goTo(this.current + 1);
  }

  pages(): Page[] {
    if (!this.total || this.total < 1) {
      return [1];
    }

    // no ellipsis
    const total = this.total;
    if (total <= maxShownPages) {
      return sequence(1, this.total);
    }

    // ellipsis on the left, on the right, or on both sides
    let beginning: Page[];
    let middle: number[];
    let end: Page[];
    const current = this.current > 0 ? this.current : 1;
    const sideDiff = (maxShownPages - 3) / 2;

    // In the following cases, round brackets are used to denote a possible current page
    // and underscore is used to denote ellipsis

    if (current - sideDiff <= 2) {
      // Case: we are close to the beginning, so no ellipsis on the left side
      // (1 2 3 4) 5 6 _ 9
      beginning = [1];
      middle = current > 1 ? sequence(2, current - 1) : [];
    } else {
      // Case: we are far from the beginning, so we need an ellipsis on the left side
      // 1 _ 3 4 (5) 6 7 _ 9
      // 1 _ 4 5 (6) 7 8 9
      beginning = [1, "_"];
      middle = sequence(current - sideDiff, sideDiff);
      if (current < total) {
        middle.push(current);
      }
    }

    if (current + sideDiff >= total - 1) {
      // Case: we are close to the end, so no ellipsis on the right side
      // 1 _ 4 5 (6 7 8 9)
      end = [total];
      if (current < total) {
        middle.push(...sequence(current + 1, total - (current + 1)));
      }
    } else {
      // Case: we are far from the end, so we need ellipsis on the right side
      // 1 _ 3 4 (5) 6 7 _ 9
      middle.push(...sequence(current + 1, sideDiff));
      end = ["_", total];
    }

    // Fill the missing pages in the middle in cases when
    // there is only one ellipsis: either on the left or on the right
    const expectedMiddleSize = maxShownPages - 2;
    const middleNeedsFilling = middle.length < expectedMiddleSize;
    if (middleNeedsFilling) {
      const hasLeftEllipsis = beginning.length === 2;
      if (hasLeftEllipsis) {
        // fill on the left
        middle = [...sequence(middle[0] - (expectedMiddleSize - middle.length), expectedMiddleSize - middle.length), ...middle];
      } else {
        // fill on the right
        middle = [...middle, ...sequence(middle[middle.length - 1] + 1, expectedMiddleSize - middle.length)];
      }
    }

    return [...beginning, ...middle, ...end];
  }
}

export const PaginationComponent: IComponentOptions = {
  templateUrl: require("./pagination.jade"),
  controller: PaginationCtrl,
  bindings: {
    current: "<",
    total: "<",
    pageChange: "&",
    mode: "@",
    width: "<?",
  },
};
